import React, { useEffect, useState, useRef, Children } from 'react';
import { useOrganization } from '../contexts/OrganizationContext';
import { MOCK_EXERCISE } from '../schemas/mockData';
import { ExerciseDefinition } from '../schemas/types';
import { TimerIcon, EyeOffIcon, ChevronLeftIcon, ChevronRightIcon, PlayCircleIcon, LayersIcon, ExternalLinkIcon, ArrowRightIcon } from 'lucide-react';
import { ImageSelection, ResultPayload } from '../schemas/types';
type Phase = 'selectExercise' | 'intro' | 'primingA' | 'setA' | 'interStimulusA' | 'primingB' | 'setB' | 'interStimulusB' | 'externalSurveyRedirect' | 'complete';
// Mock ancillary configuration
interface AncillaryConfig {
  enabled: boolean;
  mode: 'internal' | 'external';
  position: 'prepend' | 'append';
  externalSurvey?: {
    url: string;
    idParameter: string;
    returnUrl?: string;
  };
}
// Mock available exercises for the organization
const AVAILABLE_EXERCISES: ExerciseDefinition[] = [MOCK_EXERCISE, {
  exerciseId: 'ex-ukrainian-kids-v2',
  title: {
    en: 'Ukrainian Children Assessment v2',
    uk: 'Оцінка для українських дітей v2'
  },
  sets: {
    A: 'set-ukrainian-kids-a-v2',
    B: 'set-ukrainian-kids-b-v2'
  },
  timing: {
    perImageMs: 2000,
    interStimulusMs: 500,
    maxSetSeconds: 180
  },
  primingCopy: {
    A: {
      en: 'Think about the things that make you feel happy and safe...',
      uk: 'Подумайте про речі, які роблять вас щасливими та безпечними...'
    },
    B: {
      en: 'Think about the things that sometimes worry you or make you uncomfortable...',
      uk: 'Подумайте про речі, які іноді турбують вас або викликають дискомфорт...'
    }
  },
  algorithmVersion: 'v2.1.0',
  interpretationTemplate: 'ukrainian-kids-template-v2',
  locales: ['en', 'uk'],
  targetAgeBand: '5-9'
}, {
  exerciseId: 'ex-corporate-wellness',
  title: {
    en: 'Corporate Wellness Assessment',
    uk: 'Оцінка корпоративного благополуччя'
  },
  sets: {
    A: 'set-corporate-a',
    B: 'set-corporate-b'
  },
  timing: {
    perImageMs: 2500,
    interStimulusMs: 800,
    maxSetSeconds: 240
  },
  primingCopy: {
    A: {
      en: 'When I think about my work and career, I would like to feel more...',
      uk: "Коли я думаю про свою роботу та кар'єру, я хотів би відчувати більше..."
    },
    B: {
      en: 'When I think about my work and career, I would like to feel less...',
      uk: "Коли я думаю про свою роботу та кар'єру, я хотів би відчувати менше..."
    }
  },
  algorithmVersion: 'v2.0.0',
  interpretationTemplate: 'corporate-wellness-template',
  locales: ['en', 'uk'],
  targetAgeBand: 'Adults'
}];
export function RunnerPreview() {
  const {
    language,
    currentOrganization
  } = useOrganization();
  const [phase, setPhase] = useState<Phase>('selectExercise');
  const [selectedExercise, setSelectedExercise] = useState<ExerciseDefinition | null>(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [selections, setSelections] = useState<ImageSelection[]>([]);
  const [focusLossDetected, setFocusLossDetected] = useState(false);
  const [showFocusWarning, setShowFocusWarning] = useState(false);
  const [setStartTime, setSetStartTime] = useState(0);
  const [setElapsedSeconds, setSetElapsedSeconds] = useState(0);
  const [imageStartTime, setImageStartTime] = useState(0);
  const [jsonViewerOpen, setJsonViewerOpen] = useState(false);
  // Mock ancillary configuration - in real app, this would come from exercise definition
  const [ancillaryConfig] = useState<AncillaryConfig>({
    enabled: true,
    mode: 'external',
    position: 'append',
    externalSurvey: {
      url: 'https://survey.qualtrics.com/jfe/form/SV_abc123xyz',
      idParameter: 'participant_id',
      returnUrl: 'https://example.com/thank-you'
    }
  });
  // Mock participant ID for preview
  const [participantId] = useState('PREVIEW-USER-001');
  const [redirectCountdown, setRedirectCountdown] = useState(5);
  const imageTimerRef = useRef<number | null>(null);
  const setTimerRef = useRef<number | null>(null);
  const mainHeadingRef = useRef<HTMLHeadingElement>(null);
  const t = {
    title: language === 'en' ? 'Image Exercise Preview' : 'Попередній перегляд вправи',
    subtitle: language === 'en' ? 'Test the assessment experience' : 'Тестування досвіду оцінки',
    selectExercise: language === 'en' ? 'Select an Exercise to Preview' : 'Виберіть вправу для попереднього перегляду',
    selectExerciseSubtitle: language === 'en' ? 'Choose from available exercises configured for your organization' : 'Виберіть з доступних вправ, налаштованих для вашої організації',
    availableExercises: language === 'en' ? 'Available Exercises' : 'Доступні вправи',
    exerciseDetails: language === 'en' ? 'Exercise Details' : 'Деталі вправи',
    targetAge: language === 'en' ? 'Target Age' : 'Цільовий вік',
    duration: language === 'en' ? 'Duration' : 'Тривалість',
    seconds: language === 'en' ? 'seconds' : 'секунд',
    startPreview: language === 'en' ? 'Start Preview' : 'Почати попередній перегляд',
    exerciseTitle: language === 'en' ? selectedExercise?.title.en || MOCK_EXERCISE.title.en : selectedExercise?.title.uk || MOCK_EXERCISE.title.uk,
    instructions: language === 'en' ? 'You will be shown two sets of images sequentially. Click on images that resonate with you as they appear.' : "Вам буде показано два набори зображень послідовно. Натискайте на зображення, які резонують з вами, коли вони з'являються.",
    importantNote: language === 'en' ? 'Instructions:' : 'Інструкції:',
    noteText: language === 'en' ? 'Each image will be displayed for 2.5 seconds. Click or press Space/Enter on any image that resonates with you. There are no right or wrong answers.' : 'Кожне зображення буде показано протягом 2,5 секунд. Натисніть або натисніть пробіл/Enter на будь-якому зображенні, яке резонує з вами. Немає правильних чи неправильних відповідей.',
    begin: language === 'en' ? 'Begin Assessment' : 'Почати оцінку',
    primingA: language === 'en' ? selectedExercise?.primingCopy.A.en || MOCK_EXERCISE.primingCopy.A.en : selectedExercise?.primingCopy.A.uk || MOCK_EXERCISE.primingCopy.A.uk,
    primingB: language === 'en' ? selectedExercise?.primingCopy.B.en || MOCK_EXERCISE.primingCopy.B.en : selectedExercise?.primingCopy.B.uk || MOCK_EXERCISE.primingCopy.B.uk,
    continue: language === 'en' ? 'Continue' : 'Продовжити',
    setATitle: language === 'en' ? 'Set A - Click images that resonate' : 'Набір A - Натисніть зображення, які резонують',
    setBTitle: language === 'en' ? 'Set B - Click images that resonate' : 'Набір B - Натисніть зображення, які резонують',
    selected: language === 'en' ? 'selected' : 'вибрано',
    focusLoss: language === 'en' ? 'Focus loss detected and recorded' : 'Втрату фокусу виявлено та записано',
    focusWarning: language === 'en' ? 'Please keep this window in focus. Switching tabs or windows will be recorded as a quality flag.' : 'Будь ласка, тримайте це вікно в фокусі. Переключення вкладок або вікон буде записано як прапорець якості.',
    complete: language === 'en' ? 'Assessment Complete' : 'Оцінка завершена',
    thankYou: language === 'en' ? 'Thank you for completing the assessment. Your responses have been recorded.' : 'Дякуємо за завершення оцінки. Ваші відповіді записані.',
    processing: language === 'en' ? 'Results are being processed and will be available in your Knowledge Space chat.' : 'Результати обробляються та будуть доступні у вашому чаті Knowledge Space.',
    restart: language === 'en' ? 'Restart Preview' : 'Перезапустити попередній перегляд',
    image: language === 'en' ? 'Image' : 'Зображення',
    of: language === 'en' ? 'of' : 'з',
    timeRemaining: language === 'en' ? 'Time remaining' : 'Залишилось часу',
    clickToSelect: language === 'en' ? 'Click to select' : 'Натисніть для вибору',
    qualityFlags: language === 'en' ? 'Quality Flags' : 'Прапорці якості',
    focusLossFlag: language === 'en' ? 'Focus loss detected' : 'Виявлено втрату фокусу',
    bothSetsComplete: language === 'en' ? 'Both sets completed' : 'Обидва набори завершені',
    jsonViewer: language === 'en' ? 'Result Payload (QA)' : 'Результат (QA)',
    showJson: language === 'en' ? 'Show JSON' : 'Показати JSON',
    hideJson: language === 'en' ? 'Hide JSON' : 'Сховати JSON',
    redirectingToSurvey: language === 'en' ? 'Redirecting to External Survey' : 'Перенаправлення на зовнішнє опитування',
    redirectMessage: language === 'en' ? 'You will be redirected to complete an additional survey in' : 'Ви будете перенаправлені для завершення додаткового опитування через',
    redirectNow: language === 'en' ? 'Redirect Now (Preview Only)' : 'Перенаправити зараз (тільки попередній перегляд)',
    externalSurveyNote: language === 'en' ? 'Your unique participant ID will be included in the survey link for data matching.' : 'Ваш унікальний ідентифікатор учасника буде включено в посилання на опитування для зіставлення даних.'
  };
  // Focus management on phase change
  useEffect(() => {
    if (mainHeadingRef.current) {
      mainHeadingRef.current.focus();
    }
  }, [phase]);
  // Focus loss detection
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden && (phase === 'setA' || phase === 'setB' || phase === 'interStimulusA' || phase === 'interStimulusB')) {
        setFocusLossDetected(true);
        setShowFocusWarning(true);
        const timer = window.setTimeout(() => setShowFocusWarning(false), 3000);
        return () => window.clearTimeout(timer);
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [phase]);
  // Set timer
  useEffect(() => {
    if (phase === 'setA' || phase === 'setB') {
      const exercise = selectedExercise || MOCK_EXERCISE;
      if (setStartTime === 0) {
        setSetStartTime(Date.now());
      }
      setTimerRef.current = window.setInterval(() => {
        const elapsed = Math.floor((Date.now() - setStartTime) / 1000);
        setSetElapsedSeconds(elapsed);
        if (elapsed >= exercise.timing.maxSetSeconds) {
          if (phase === 'setA') {
            setPhase('primingB');
            setCurrentImageIndex(0);
            setSetStartTime(0);
            setSetElapsedSeconds(0);
          } else {
            // Check if external survey is configured for append position
            if (ancillaryConfig.enabled && ancillaryConfig.mode === 'external' && ancillaryConfig.position === 'append') {
              setPhase('externalSurveyRedirect');
            } else {
              setPhase('complete');
            }
          }
        }
      }, 100);
      return () => {
        if (setTimerRef.current !== null) {
          window.clearInterval(setTimerRef.current);
          setTimerRef.current = null;
        }
      };
    }
  }, [phase, setStartTime, selectedExercise, ancillaryConfig]);
  // Image display and auto-advance timer
  useEffect(() => {
    if (phase === 'setA' || phase === 'setB') {
      const exercise = selectedExercise || MOCK_EXERCISE;
      const startTime = Date.now();
      setImageStartTime(startTime);
      imageTimerRef.current = window.setTimeout(() => {
        const reactionTimeMs = Date.now() - startTime;
        setSelections(prev => [...prev, {
          imageId: `img-${phase === 'setA' ? 'A' : 'B'}-${currentImageIndex + 1}`,
          set: phase === 'setA' ? 'A' : 'B',
          selected: false,
          reactionTimeMs
        }]);
        if (phase === 'setA') {
          setPhase('interStimulusA');
        } else {
          setPhase('interStimulusB');
        }
      }, exercise.timing.perImageMs);
      return () => {
        if (imageTimerRef.current !== null) {
          window.clearTimeout(imageTimerRef.current);
          imageTimerRef.current = null;
        }
      };
    }
  }, [phase, currentImageIndex, selectedExercise]);
  // Inter-stimulus timer
  useEffect(() => {
    if (phase === 'interStimulusA' || phase === 'interStimulusB') {
      const exercise = selectedExercise || MOCK_EXERCISE;
      const timer = window.setTimeout(() => {
        if (currentImageIndex < 35) {
          setCurrentImageIndex(currentImageIndex + 1);
          if (phase === 'interStimulusA') {
            setPhase('setA');
          } else {
            setPhase('setB');
          }
        } else {
          if (phase === 'interStimulusA') {
            setPhase('primingB');
            setCurrentImageIndex(0);
            setSetStartTime(0);
            setSetElapsedSeconds(0);
          } else {
            // Check if external survey is configured for append position
            if (ancillaryConfig.enabled && ancillaryConfig.mode === 'external' && ancillaryConfig.position === 'append') {
              setPhase('externalSurveyRedirect');
            } else {
              setPhase('complete');
            }
          }
        }
      }, exercise.timing.interStimulusMs);
      return () => window.clearTimeout(timer);
    }
  }, [phase, currentImageIndex, selectedExercise, ancillaryConfig]);
  // Redirect countdown timer
  useEffect(() => {
    if (phase === 'externalSurveyRedirect' && redirectCountdown > 0) {
      const timer = window.setTimeout(() => {
        setRedirectCountdown(redirectCountdown - 1);
      }, 1000);
      return () => window.clearTimeout(timer);
    } else if (phase === 'externalSurveyRedirect' && redirectCountdown === 0) {
      // In real app, this would actually redirect
      // window.location.href = generatedRedirectUrl
      // For preview, we'll just move to complete
      const timer = window.setTimeout(() => {
        setPhase('complete');
      }, 1000);
      return () => window.clearTimeout(timer);
    }
  }, [phase, redirectCountdown]);
  const handleImageClick = () => {
    if (phase !== 'setA' && phase !== 'setB') return;
    if (imageTimerRef.current !== null) {
      window.clearTimeout(imageTimerRef.current);
      imageTimerRef.current = null;
    }
    const reactionTimeMs = Date.now() - imageStartTime;
    setSelections(prev => [...prev, {
      imageId: `img-${phase === 'setA' ? 'A' : 'B'}-${currentImageIndex + 1}`,
      set: phase === 'setA' ? 'A' : 'B',
      selected: true,
      reactionTimeMs
    }]);
    if (phase === 'setA') {
      setPhase('interStimulusA');
    } else {
      setPhase('interStimulusB');
    }
  };
  const handleImageKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault();
      handleImageClick();
    }
  };
  const handleSelectExercise = (exercise: ExerciseDefinition) => {
    setSelectedExercise(exercise);
    setPhase('intro');
  };
  const handleBegin = () => {
    setPhase('primingA');
  };
  const handleContinueFromPrimingA = () => {
    setPhase('setA');
    setSetStartTime(Date.now());
  };
  const handleContinueFromPrimingB = () => {
    setPhase('setB');
    setSetStartTime(Date.now());
  };
  const handleRestart = () => {
    setPhase('selectExercise');
    setSelectedExercise(null);
    setCurrentImageIndex(0);
    setSelections([]);
    setFocusLossDetected(false);
    setShowFocusWarning(false);
    setSetStartTime(0);
    setSetElapsedSeconds(0);
    setJsonViewerOpen(false);
    setRedirectCountdown(5);
  };
  const handleRedirectNow = () => {
    setRedirectCountdown(0);
  };
  // Generate redirect URL with participant ID
  const generatedRedirectUrl = ancillaryConfig.externalSurvey ? `${ancillaryConfig.externalSurvey.url}${ancillaryConfig.externalSurvey.url.includes('?') ? '&' : '?'}${ancillaryConfig.externalSurvey.idParameter}=${participantId}` : '';
  // Generate ResultPayload for JSON viewer
  const resultPayload: ResultPayload = {
    externalUserId: participantId,
    exerciseId: selectedExercise?.exerciseId || MOCK_EXERCISE.exerciseId,
    selections,
    qualityFlags: {
      focusLoss: focusLossDetected,
      incompleteSetA: selections.filter(s => s.set === 'A').length < 36,
      incompleteSetB: selections.filter(s => s.set === 'B').length < 36,
      insufficientSelections: selections.filter(s => s.selected).length < 10
    },
    computed: {
      positiveAffect: 72,
      negativeAffect: 28,
      arousal: 58,
      confidence: 89
    },
    deliveredToSpaces: {
      orgId: currentOrganization.spacesOrgId,
      chatbotId: currentOrganization.defaultChatbotId || 'N/A',
      pushedAt: new Date().toISOString()
    },
    completedAt: new Date().toISOString()
  };
  // Exercise Selection Screen
  if (phase === 'selectExercise') {
    return <div className="p-8 bg-gray-50 min-h-screen" data-id="element-2490">
        <div className="max-w-6xl mx-auto" data-id="element-2491">
          {/* Header */}
          <div className="mb-8" data-id="element-2492">
            <h1 ref={mainHeadingRef} tabIndex={-1} className="text-3xl font-bold text-gray-900" data-id="element-2493">
              {t.selectExercise}
            </h1>
            <p className="text-gray-600 mt-2" data-id="element-2494">{t.selectExerciseSubtitle}</p>
          </div>
          {/* Exercise Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-2495">
            {AVAILABLE_EXERCISES.map(exercise => <div key={exercise.exerciseId} className="bg-white rounded-xl shadow-sm border-2 border-gray-200 overflow-hidden hover:shadow-md hover:border-blue-300 transition-all flex flex-col" data-id="element-2496">
                <div className="bg-gradient-to-r from-blue-500 to-blue-600 px-6 py-4" data-id="element-2497">
                  <div className="flex items-center gap-3" data-id="element-2498">
                    <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0" data-id="element-2499">
                      <LayersIcon className="w-5 h-5 text-white" data-id="element-2500" />
                    </div>
                    <h3 className="text-lg font-bold text-white line-clamp-2 min-h-[3.5rem] flex items-center" title={language === 'en' ? exercise.title.en : exercise.title.uk} data-id="element-2501">
                      {language === 'en' ? exercise.title.en : exercise.title.uk}
                    </h3>
                  </div>
                </div>
                <div className="p-6 flex-1 flex flex-col" data-id="element-2502">
                  <div className="space-y-3 mb-6 flex-1" data-id="element-2503">
                    <div className="flex items-center justify-between text-sm" data-id="element-2504">
                      <span className="text-gray-600 font-medium" data-id="element-2505">
                        {t.targetAge}:
                      </span>
                      <span className="font-semibold text-gray-900" data-id="element-2506">
                        {exercise.targetAgeBand || 'All Ages'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-sm" data-id="element-2507">
                      <span className="text-gray-600 font-medium" data-id="element-2508">
                        {t.duration}:
                      </span>
                      <span className="font-semibold text-gray-900" data-id="element-2509">
                        ~{Math.floor(exercise.timing.maxSetSeconds / 60)} min
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-sm" data-id="element-2510">
                      <span className="text-gray-600 font-medium" data-id="element-2511">Images:</span>
                      <span className="font-semibold text-gray-900" data-id="element-2512">
                        72 total
                      </span>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2 mb-6" data-id="element-2513">
                    {exercise.locales.map(locale => <span key={locale} className="px-2 py-1 bg-purple-50 text-purple-700 rounded text-xs font-semibold uppercase border border-purple-200" data-id="element-2514">
                        {locale}
                      </span>)}
                  </div>
                  <button onClick={() => handleSelectExercise(exercise)} className="w-full px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-500 text-white rounded-xl hover:from-blue-700 hover:to-blue-600 transition-all font-semibold shadow-md hover:shadow-lg flex items-center justify-center gap-2" data-id="element-2515">
                    <PlayCircleIcon className="w-5 h-5" data-id="element-2516" />
                    {t.startPreview}
                  </button>
                </div>
              </div>)}
          </div>
        </div>
      </div>;
  }
  if (phase === 'intro') {
    const exercise = selectedExercise || MOCK_EXERCISE;
    return <div className="fixed inset-0 bg-gradient-to-br from-gray-50 to-blue-50 flex items-center justify-center p-6" style={{
      marginLeft: '256px'
    }} data-id="element-2517">
        <div className="max-w-2xl w-full bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden" data-id="element-2518">
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-8 py-6" data-id="element-2519">
            <h1 ref={mainHeadingRef} tabIndex={-1} className="text-2xl font-bold text-white text-center" data-id="element-2520">
              {t.exerciseTitle}
            </h1>
          </div>
          <div className="p-8" data-id="element-2521">
            <p className="text-gray-700 mb-6 text-center text-lg" data-id="element-2522">
              {t.instructions}
            </p>
            <div className="bg-blue-50 border-l-4 border-blue-600 rounded-r-lg p-4 mb-6" data-id="element-2523">
              <p className="text-sm text-blue-900" data-id="element-2524">
                <strong data-id="element-2525">{t.importantNote}</strong> {t.noteText}
              </p>
            </div>
            <div className="bg-yellow-50 border-l-4 border-yellow-600 rounded-r-lg p-4 mb-8" data-id="element-2526">
              <p className="text-sm text-yellow-900" data-id="element-2527">{t.focusWarning}</p>
            </div>
            <button onClick={handleBegin} className="w-full px-6 py-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all font-semibold text-lg shadow-lg hover:shadow-xl transform hover:-translate-y-0.5" data-id="element-2528">
              {t.begin}
            </button>
          </div>
        </div>
      </div>;
  }
  if (phase === 'primingA' || phase === 'primingB') {
    return <div className="fixed inset-0 bg-gradient-to-br from-gray-50 to-blue-50 flex items-center justify-center p-6" style={{
      marginLeft: '256px'
    }} data-id="element-2529">
        <div className="max-w-3xl w-full bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden" data-id="element-2530">
          <div className="px-12 py-16" style={{
          backgroundColor: phase === 'primingA' ? '#f0fdf4' : '#fef2f2',
          borderBottom: `4px solid ${phase === 'primingA' ? '#86efac' : '#fca5a5'}`
        }} data-id="element-2531">
            <p className="text-2xl text-gray-900 italic text-center font-medium leading-relaxed" data-id="element-2532">
              {phase === 'primingA' ? t.primingA : t.primingB}
            </p>
          </div>
          <div className="p-8" data-id="element-2533">
            <button onClick={phase === 'primingA' ? handleContinueFromPrimingA : handleContinueFromPrimingB} className="w-full px-6 py-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all font-semibold text-lg shadow-lg hover:shadow-xl transform hover:-translate-y-0.5" data-id="element-2534">
              {t.continue}
            </button>
          </div>
        </div>
      </div>;
  }
  if (phase === 'externalSurveyRedirect') {
    return <div className="fixed inset-0 bg-gradient-to-br from-blue-50 to-purple-50 flex items-center justify-center p-6" style={{
      marginLeft: '256px'
    }} data-id="element-2535">
        <div className="max-w-2xl w-full bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden" data-id="element-2536">
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 px-8 py-6" data-id="element-2537">
            <div className="flex items-center justify-center gap-3" data-id="element-2538">
              <ExternalLinkIcon className="w-8 h-8 text-white" data-id="element-2539" />
              <h1 ref={mainHeadingRef} tabIndex={-1} className="text-2xl font-bold text-white text-center" data-id="element-2540">
                {t.redirectingToSurvey}
              </h1>
            </div>
          </div>
          <div className="p-8 space-y-6" data-id="element-2541">
            {/* Countdown Display */}
            <div className="flex flex-col items-center gap-4" data-id="element-2542">
              <div className="w-32 h-32 rounded-full bg-gradient-to-br from-blue-100 to-purple-100 flex items-center justify-center" data-id="element-2543">
                <span className="text-6xl font-bold text-blue-600" data-id="element-2544">
                  {redirectCountdown}
                </span>
              </div>
              <p className="text-lg text-gray-700 text-center" data-id="element-2545">
                {t.redirectMessage}
              </p>
            </div>
            {/* Survey URL Preview */}
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200" data-id="element-2546">
              <label className="block text-sm font-semibold text-gray-900 mb-2" data-id="element-2547">
                External Survey URL
              </label>
              <code className="block text-xs bg-white p-3 rounded border border-gray-300 break-all" data-id="element-2548">
                {generatedRedirectUrl}
              </code>
            </div>
            {/* Info Message */}
            <div className="bg-blue-50 border-l-4 border-blue-600 rounded-r-lg p-4" data-id="element-2549">
              <p className="text-sm text-blue-900" data-id="element-2550">{t.externalSurveyNote}</p>
            </div>
            {/* Preview Controls */}
            <div className="flex gap-3" data-id="element-2551">
              <button onClick={handleRedirectNow} className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all font-medium flex items-center justify-center gap-2" data-id="element-2552">
                <ArrowRightIcon className="w-5 h-5" data-id="element-2553" />
                {t.redirectNow}
              </button>
            </div>
            {/* Preview Note */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3" data-id="element-2554">
              <p className="text-xs text-yellow-900 text-center" data-id="element-2555">
                <strong data-id="element-2556">Preview Mode:</strong> In production, users would be
                automatically redirected to the external survey. Click "Redirect
                Now" to skip the countdown.
              </p>
            </div>
          </div>
        </div>
      </div>;
  }
  if (phase === 'complete') {
    const setASelections = selections.filter(s => s.set === 'A');
    const setBSelections = selections.filter(s => s.set === 'B');
    const setASelected = setASelections.filter(s => s.selected).length;
    const setBSelected = setBSelections.filter(s => s.selected).length;
    return <div className="p-8" id="main-content" data-id="element-2557">
        <div className="mb-8" data-id="element-2558">
          <h1 ref={mainHeadingRef} tabIndex={-1} className="text-3xl font-semibold text-gray-900" data-id="element-2559">
            {t.title}
          </h1>
          <p className="text-gray-600 mt-1" data-id="element-2560">{t.subtitle}</p>
        </div>
        <div className="flex gap-6" data-id="element-2561">
          <div className="flex-1 max-w-2xl bg-white rounded-lg shadow-sm border border-gray-200 p-8" data-id="element-2562">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-2563">
              <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-2564">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" data-id="element-2565" />
              </svg>
            </div>
            <h2 className="text-2xl font-semibold text-gray-900 mb-2 text-center" data-id="element-2566">
              {t.complete}
            </h2>
            <p className="text-gray-600 mb-6 text-center" data-id="element-2567">{t.thankYou}</p>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6" data-id="element-2568">
              <p className="text-sm text-blue-900 text-center" data-id="element-2569">
                {t.processing}
              </p>
            </div>
            <div className="bg-gray-50 rounded-lg p-4 mb-6 space-y-2" data-id="element-2570">
              <div className="text-sm text-gray-700" data-id="element-2571">
                <strong data-id="element-2572">Set A:</strong> {setASelected} / {setASelections.length}{' '}
                {t.selected}
              </div>
              <div className="text-sm text-gray-700" data-id="element-2573">
                <strong data-id="element-2574">Set B:</strong> {setBSelected} / {setBSelections.length}{' '}
                {t.selected}
              </div>
              <div className="text-sm text-gray-700" data-id="element-2575">
                <strong data-id="element-2576">Total:</strong> {setASelected + setBSelected} /{' '}
                {selections.length} {t.selected}
              </div>
            </div>
            <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6" data-id="element-2577">
              <h3 className="text-sm font-semibold text-gray-900 mb-2" data-id="element-2578">
                {t.qualityFlags}
              </h3>
              <div className="space-y-2" data-id="element-2579">
                {focusLossDetected && <div className="flex items-center gap-2 text-sm text-yellow-800" data-id="element-2580">
                    <EyeOffIcon className="w-4 h-4" data-id="element-2581" />
                    {t.focusLossFlag}
                  </div>}
                {setASelections.length === 36 && setBSelections.length === 36 && <div className="flex items-center gap-2 text-sm text-green-800" data-id="element-2582">
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-2583">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" data-id="element-2584" />
                      </svg>
                      {t.bothSetsComplete}
                    </div>}
              </div>
            </div>
            <button onClick={handleRestart} className="w-full px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-2585">
              {t.restart}
            </button>
          </div>
          <div className={`transition-all duration-300 ${jsonViewerOpen ? 'w-96' : 'w-12'}`} data-id="element-2586">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 h-full" data-id="element-2587">
              <button onClick={() => setJsonViewerOpen(!jsonViewerOpen)} className="w-full p-3 flex items-center justify-center gap-2 border-b border-gray-200 hover:bg-gray-50 transition-colors" data-id="element-2588">
                {jsonViewerOpen ? <>
                    <ChevronRightIcon className="w-5 h-5 text-gray-600" data-id="element-2589" />
                    <span className="text-sm font-medium text-gray-700" data-id="element-2590">
                      {t.hideJson}
                    </span>
                  </> : <ChevronLeftIcon className="w-5 h-5 text-gray-600" data-id="element-2591" />}
              </button>
              {jsonViewerOpen && <div className="p-4 overflow-auto" style={{
              maxHeight: 'calc(100vh - 200px)'
            }} data-id="element-2592">
                  <h3 className="text-sm font-semibold text-gray-900 mb-3" data-id="element-2593">
                    {t.jsonViewer}
                  </h3>
                  <pre className="text-xs bg-gray-900 text-green-400 p-4 rounded-lg overflow-x-auto" data-id="element-2594">
                    {JSON.stringify(resultPayload, null, 2)}
                  </pre>
                </div>}
            </div>
          </div>
        </div>
      </div>;
  }
  if (phase === 'interStimulusA' || phase === 'interStimulusB') {
    const exercise = selectedExercise || MOCK_EXERCISE;
    return <div className="fixed inset-0 bg-gray-900 flex flex-col" style={{
      marginLeft: '256px'
    }} data-id="element-2595">
        {/* Compact header */}
        <div className="bg-gray-800 border-b border-gray-700 px-6 py-3" data-id="element-2596">
          <div className="flex items-center justify-between" data-id="element-2597">
            <div className="flex items-center gap-4" data-id="element-2598">
              <div className="text-white font-medium" data-id="element-2599">
                {phase === 'interStimulusA' ? 'Set A' : 'Set B'}
              </div>
              <div className="text-gray-400 text-sm" data-id="element-2600">
                {t.image} {currentImageIndex + 1} / 36
              </div>
            </div>
            <div className="flex items-center gap-2 text-white font-semibold" data-id="element-2601">
              <TimerIcon className="w-5 h-5" data-id="element-2602" />
              <span data-id="element-2603">{exercise.timing.maxSetSeconds - setElapsedSeconds}s</span>
            </div>
          </div>
        </div>
        {/* Focus warning */}
        {showFocusWarning && <div className="absolute top-20 left-1/2 transform -translate-x-1/2 z-50" data-id="element-2604">
            <div className="bg-yellow-500 text-yellow-900 px-6 py-3 rounded-lg shadow-lg flex items-center gap-3" data-id="element-2605">
              <EyeOffIcon className="w-5 h-5" data-id="element-2606" />
              <span className="font-medium" data-id="element-2607">{t.focusLoss}</span>
            </div>
          </div>}
        {/* Full screen centered content */}
        <div className="flex-1 flex items-center justify-center" data-id="element-2608">
          <div className="text-gray-500 text-2xl animate-pulse" data-id="element-2609">...</div>
        </div>
      </div>;
  }
  // Active image display (setA or setB)
  const exercise = selectedExercise || MOCK_EXERCISE;
  const currentSet = phase === 'setA' ? 'A' : 'B';
  const primingText = phase === 'setA' ? t.primingA : t.primingB;
  const setSelectionsList = selections.filter(s => s.set === currentSet);
  return <div className="fixed inset-0 bg-gray-900 flex flex-col" style={{
    marginLeft: '256px'
  }} data-id="element-2610">
      {/* Compact header with priming text */}
      <div className="border-b px-6 py-3" style={{
      backgroundColor: phase === 'setA' ? '#065f46' : '#7f1d1d',
      borderColor: phase === 'setA' ? '#059669' : '#dc2626'
    }} data-id="element-2611">
        <div className="flex items-center justify-between mb-2" data-id="element-2612">
          <div className="flex items-center gap-4" data-id="element-2613">
            <div className="text-white font-medium" data-id="element-2614">
              {phase === 'setA' ? 'Set A' : 'Set B'}
            </div>
            <div className="text-gray-300 text-sm" data-id="element-2615">
              {t.image} {currentImageIndex + 1} / 36
            </div>
            <div className="text-gray-300 text-sm" data-id="element-2616">
              {setSelectionsList.filter(s => s.selected).length} {t.selected}
            </div>
          </div>
          <div className="flex items-center gap-2 text-white font-semibold text-lg" data-id="element-2617">
            <TimerIcon className="w-5 h-5" data-id="element-2618" />
            <span data-id="element-2619">{exercise.timing.maxSetSeconds - setElapsedSeconds}s</span>
          </div>
        </div>
        <p className="text-sm text-gray-200 italic text-center" data-id="element-2620">
          {primingText}
        </p>
      </div>
      {/* Focus warning */}
      {showFocusWarning && <div className="absolute top-24 left-1/2 transform -translate-x-1/2 z-50" data-id="element-2621">
          <div className="bg-yellow-500 text-yellow-900 px-6 py-3 rounded-lg shadow-lg flex items-center gap-3" data-id="element-2622">
            <EyeOffIcon className="w-5 h-5" data-id="element-2623" />
            <span className="font-medium" data-id="element-2624">{t.focusLoss}</span>
          </div>
        </div>}
      {/* Full screen image display */}
      <div className="flex-1 flex items-center justify-center p-8" data-id="element-2625">
        <button onClick={handleImageClick} onKeyDown={handleImageKeyDown} className="w-full h-full max-w-4xl max-h-[70vh] bg-gradient-to-br from-gray-700 to-gray-800 rounded-2xl hover:ring-4 hover:ring-blue-500 transition-all cursor-pointer flex items-center justify-center focus:ring-4 focus:ring-blue-500 focus:outline-none shadow-2xl" aria-label={t.clickToSelect} style={{
        aspectRatio: '1/1'
      }} data-id="element-2626">
          <div className="text-gray-400 text-lg font-medium" data-id="element-2627">
            {t.clickToSelect}
          </div>
        </button>
      </div>
      {/* Bottom hint */}
      <div className="bg-gray-800 border-t border-gray-700 px-6 py-3 text-center" data-id="element-2628">
        <p className="text-sm text-gray-400" data-id="element-2629">{t.clickToSelect}</p>
      </div>
    </div>;
}